import React, { memo, useCallback, useEffect, useState } from 'react';
import { Chip, Listbox, ListboxItem } from '@heroui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faEllipsis, faTrash, faUndoAlt, faUser } from '@fortawesome/free-solid-svg-icons';
import SearchboxInput from '../Searchbox-Input';
import { IFilterForm } from '../../filterTypes';
import LoadingSpinner from '../../../../shared/components/loading-spinner/LoadingSpinner';
import TooltipLink from './Tooltip-Link';

export interface ISearchableListboxProps {
	items: IFilterForm[] | null;
	clickItemLink: (id: number | string) => void;
	editItem: (item: IFilterForm) => void;
	deleteItem?: (item: IFilterForm) => void;
	refreshItem: (id: number) => void;
	selectedItem?: IFilterForm | null;
	type: string;
	isDetailPanelOpen: boolean;
	isLoading: boolean;
	spinnerLabel?: string;
}

export const SearchableListbox: React.FC<ISearchableListboxProps> = memo(
	({
		isLoading,
		spinnerLabel,
		items,
		selectedItem,
		editItem,
		refreshItem,
		deleteItem,
		type,
		clickItemLink,
		isDetailPanelOpen,
	}) => {
		const [searchTerm, setSearchTerm] = useState<string>('');
		const [filteredItems, setFilteredItems] = useState<IFilterForm[] | []>(items ?? []);

		useEffect(() => {
			const filteredItems = items?.filter((item: IFilterForm) => item.name.toLowerCase().includes(searchTerm.toLowerCase()));
			if (filteredItems && filteredItems.length > 0) {
				setFilteredItems(filteredItems);
			}
		}, [searchTerm, items]);

		const renderEndContent = useCallback(
			(item: IFilterForm) => (
				<>
					{item.isDraft && item.id === null ? (
						<Chip className="rounded h-4 text-xs" color="warning">
							Draft
						</Chip>
					) : item.isDraft && item.id && item.id > 0 ? (
						<Chip className="rounded h-4 text-xs" color="danger">
							Unsaved
						</Chip>
					) : null}
					{isDetailPanelOpen && selectedItem?.id === item.id && selectedItem.name === item.name ? (
						<>
							{item.id !== null && item.isDraft ? (
								<FontAwesomeIcon
									size="xs"
									onClick={(e) => {
										e.stopPropagation();
										refreshItem(item.id ?? 0);
									}}
									className=" cursor-pointer text-danger"
									icon={faUndoAlt}
								/>
							) : (
								<FontAwesomeIcon size="xs" className="text-black" icon={faEllipsis} />
							)}
							{((type === 'team' && item?.isOwnFilter === true) || type !== 'team') && (
								<FontAwesomeIcon
									size="xs"
									onClick={(e) => {
										e.stopPropagation();
										deleteItem && deleteItem(item);
									}}
									className="cursor-pointer text-danger"
									icon={faTrash}
								/>
							)}
						</>
					) : (
						<>
							{type === 'team' && item?.isOwnFilter === true ? <FontAwesomeIcon size="xs" icon={faUser} /> : null}
							<FontAwesomeIcon
								size="xs"
								onClick={(e) => {
									e.stopPropagation();
									editItem(item);
								}}
								className="cursor-pointer text-black"
								icon={faEdit}
							/>
						</>
					)}
				</>
			),
			[isDetailPanelOpen, selectedItem?.id, selectedItem?.name, type, refreshItem, deleteItem, editItem],
		);

		return (
			<>
				<SearchboxInput setSearchTerm={setSearchTerm} />
				{isLoading === true ? (
					<LoadingSpinner size="sm" label={spinnerLabel} spinnerClassName="flex-auto" />
				) : (
					<Listbox
						className="flex-auto mt-2 overflow-y-auto border"
						style={{ scrollbarGutter: 'stable' }}
						hideSelectedIcon
						selectionMode="none"
						aria-label="Listbox"
						items={filteredItems ?? []}
						key={'load-filter'}
					>
						{filteredItems.map((item: IFilterForm) => {
							const isSelected =
								(item.id !== null && selectedItem?.id === item.id) ||
								(item.id === null &&
									selectedItem?.tempId !== null &&
									selectedItem?.tempId === item.tempId &&
									item.isDraft === true);
							return (
								<ListboxItem
									key={item.id ?? item.tempId ?? 'undefined-id'}
									// onSelect={() => item.id !== null && clickItemLink(item.id)}
									aria-label={item.id?.toString() ?? item.tempId ?? 'undefined-id'}									
									className={`cursor-default rounded h-6 text-small data-selectable:focus:bg-transparent ${isSelected ? 'bg-[#D4D4D8]' : 'bg-transparent'}`}
								>
									<div className="flex items-center justify-between w-full">
										<div className="flex-1 min-w-0 overflow-hidden mr-2">
											<TooltipLink item={item} clickItemLink={clickItemLink} />
										</div>
										<div className="flex items-center gap-1 shrink-0">{renderEndContent(item)}</div>
									</div>
								</ListboxItem>
							);
						})}
					</Listbox>
				)}
			</>
		);
	},
);

export default SearchableListbox;
